/**
 * Testimonial carousel controls.
 *
 * The track is a scroll-snap scroller, so it is already swipeable and keyboard
 * scrollable without this script. This only adds the arrow buttons and dots,
 * and keeps the active dot in sync with whatever slide is centred.
 */
class UrlTestimonials extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.dots = Array.from(this.querySelectorAll('[data-dots] button'));
    this.arrows = Array.from(this.querySelectorAll('[data-direction]'));

    this.arrows.forEach((button) => {
      button.addEventListener('click', () => {
        this.step(button.dataset.direction === 'next' ? 1 : -1);
      });
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.goTo(Number(dot.dataset.index));
      });
    });

    // Scroll position is the source of truth, so read it back rather than
    // tracking an index that could drift when the user swipes.
    this.track.addEventListener('scroll', () => {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.syncDots());
    });

    this.syncDots();
  }

  get current() {
    const width = this.track.clientWidth;
    if (!width) return 0;
    return Math.round(this.track.scrollLeft / width);
  }

  goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    this.track.scrollTo({ left: clamped * this.track.clientWidth, behavior: 'smooth' });
  }

  step(delta) {
    // Wrap around at both ends so the arrows never dead-end.
    const next = (this.current + delta + this.slides.length) % this.slides.length;
    this.goTo(next);
  }

  syncDots() {
    const active = this.current;
    this.dots.forEach((dot, index) => {
      if (index === active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('url-testimonials')) {
  customElements.define('url-testimonials', UrlTestimonials);
}
